import { createAsyncThunk } from "@reduxjs/toolkit";
import {
    getAllSalaryAttachmentsByCaseId,
    getSalaryAttachmentById,
    createSalaryAttachment,
    updateSalaryAttachment,
    deleteSalaryAttachment,
    formatSalaryAttachmentData,
    parseAttachmentIds,
    validateSalaryAttachmentData
} from "../../../api/caseSalaryAttachment_api";

// =========== GET ALL SALARY ATTACHMENTS BY CASE ID ===========
export const getAllSalaryAttachmentsByCaseIdAction = createAsyncThunk(
    "salaryAttachment/getAllSalaryAttachmentsByCaseId",
    async (caseDetailsID, { rejectWithValue }) => {
        try {
            const result = await getAllSalaryAttachmentsByCaseId(caseDetailsID);

            if (result.success) {
                return {
                    caseDetailsID,
                    salaryAttachments: result.data,
                    assetOwnedBy: result.assetOwnedBy,
                    count: result.count,
                    message: result.message
                };
            } else {
                return rejectWithValue({
                    message: result.message,
                    error: result.error
                });
            }
        } catch (error) {
            return rejectWithValue({
                message: "Failed to fetch salary attachments",
                error: error.message
            });
        }
    }
);

// =========== GET SINGLE SALARY ATTACHMENT BY ID ===========
export const getSalaryAttachmentByIdAction = createAsyncThunk(
    "salaryAttachment/getSalaryAttachmentById",
    async (salaryAttachmentID, { rejectWithValue }) => {
        try {
            const result = await getSalaryAttachmentById(salaryAttachmentID);

            if (result.success) {
                return {
                    salaryAttachment: result.data,
                    message: result.message
                };
            } else {
                return rejectWithValue({
                    message: result.message,
                    error: result.error
                });
            }
        } catch (error) {
            return rejectWithValue({
                message: "Failed to fetch salary attachment",
                error: error.message
            });
        }
    }
);

// =========== CREATE SALARY ATTACHMENT ===========
export const createSalaryAttachmentAction = createAsyncThunk(
    "salaryAttachment/createSalaryAttachment",
    async ({ caseDetailsID, formData }, { rejectWithValue }) => {
        try {
            let dataToSend;

            if (formData instanceof FormData) {
                dataToSend = formData;
                if (!dataToSend.has('caseDetailsID')) {
                    dataToSend.append('caseDetailsID', caseDetailsID);
                }
            } else {
                dataToSend = formatSalaryAttachmentData({
                    ...formData,
                    caseDetailsID: caseDetailsID || formData.caseDetailsID
                });
            }

            const result = await createSalaryAttachment(dataToSend);

            if (result.success) {
                return {
                    salaryAttachment: result.data,
                    message: result.message
                };
            } else {
                return rejectWithValue({
                    message: result.message,
                    error: result.error
                });
            }
        } catch (error) {
            return rejectWithValue({
                message: "Failed to create salary attachment",
                error: error.message
            });
        }
    }
);

// =========== UPDATE SALARY ATTACHMENT ===========
export const updateSalaryAttachmentAction = createAsyncThunk(
    "salaryAttachment/updateSalaryAttachment",
    async ({ salaryAttachmentID, salaryData, caseDetailsID }, { rejectWithValue }) => {
        try {
            let dataToSend;

            if (salaryData instanceof FormData) {
                dataToSend = salaryData;
                if (!dataToSend.has('caseDetailsID') && caseDetailsID) {
                    dataToSend.append('caseDetailsID', caseDetailsID);
                }
            } else {
                dataToSend = formatSalaryAttachmentData({
                    ...salaryData,
                    caseDetailsID,
                });
            }

            const result = await updateSalaryAttachment(salaryAttachmentID, dataToSend);

            if (result.success) {
                return {
                    salaryAttachmentID,
                    salaryAttachment: result.data,
                    message: result.message
                };
            } else {
                return rejectWithValue({
                    message: result.message,
                    error: result.error
                });
            }
        } catch (error) {
            return rejectWithValue({
                message: "Failed to update salary attachment",
                error: error.message
            });
        }
    }
);

// =========== DELETE SALARY ATTACHMENT ===========
export const deleteSalaryAttachmentAction = createAsyncThunk(
    "salaryAttachment/deleteSalaryAttachment",
    async ({ salaryAttachmentID, updatedBy }, { rejectWithValue }) => {
        try {
            const result = await deleteSalaryAttachment(salaryAttachmentID, updatedBy);

            if (result.success) {
                return {
                    salaryAttachmentID,
                    message: result.message
                };
            } else {
                return rejectWithValue({
                    message: result.message,
                    error: result.error
                });
            }
        } catch (error) {
            return rejectWithValue({
                message: "Failed to delete salary attachment",
                error: error.message
            });
        }
    }
);

// =========== FORMAT SALARY ATTACHMENT DATA (Non-async utility action) ===========
export const formatSalaryAttachmentDataAction = (salaryData) => ({
    type: "salaryAttachment/formatSalaryAttachmentData",
    payload: formatSalaryAttachmentData(salaryData)
});

// =========== PARSE ATTACHMENT IDs (Non-async utility action) ===========
export const parseAttachmentIdsAction = (attachmentField) => ({
    type: "salaryAttachment/parseAttachmentIds",
    payload: parseAttachmentIds(attachmentField)
});

// =========== VALIDATE SALARY ATTACHMENT DATA (Non-async utility action) ===========
export const validateSalaryAttachmentDataAction = (salaryData) => ({
    type: "salaryAttachment/validateSalaryAttachmentData",
    payload: validateSalaryAttachmentData(salaryData)
});

// =========== CLEAR SALARY ATTACHMENT STATE ===========
export const clearSalaryAttachmentStateAction = () => ({
    type: "salaryAttachment/clearState"
});

// =========== SET SELECTED SALARY ATTACHMENT ===========
export const setSelectedSalaryAttachmentAction = (salaryAttachment) => ({
    type: "salaryAttachment/setSelectedSalaryAttachment",
    payload: salaryAttachment
});
